import { Button, Modal, Typography } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';

const { Title, Paragraph, Text } = Typography;

type ForgotPasswordConfirmModalProps = {
  isOpen: boolean;
  isForgotPasswordProcessImplemented: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onStartReset: () => void;
  onSignOut: () => void;
};

const MotionWrapper = ({ children, viewKey }: { children: React.ReactNode; viewKey: string }) => (
  <AnimatePresence mode="wait">
    <motion.div
      key={viewKey}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

export const ForgotPasswordConfirmModal = ({
  isOpen,
  isForgotPasswordProcessImplemented,
  isSubmitting,
  onCancel,
  onStartReset,
  onSignOut,
}: ForgotPasswordConfirmModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal open={isOpen} onCancel={onCancel} footer={null} centered destroyOnHidden>
      <MotionWrapper viewKey={isForgotPasswordProcessImplemented ? 'success' : 'confirm'}>
        {!isForgotPasswordProcessImplemented ? (
          <div className="flex flex-col gap-3">
            <Title level={4} className="!mb-1">
              {t('layout-forgot-password-modal-title')}
            </Title>

            <Paragraph className="!mb-1">{t('layout-forgot-password-modal-intro')}</Paragraph>

            <Paragraph className="!mb-1">
              <Trans i18nKey="layout-forgot-password-modal-effect" components={{ b: <Text strong /> }} />
            </Paragraph>

            <Paragraph type="secondary" className="!mb-4">
              {t('layout-forgot-password-modal-email-note')}
            </Paragraph>

            <div className="flex justify-end gap-2 mt-2">
              <Button onClick={onCancel}>{t('common-cancel')}</Button>
              <Button type="primary" danger onClick={onStartReset} loading={isSubmitting}>
                {t('common-continue')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Title level={4} className="!mb-1">
              {t('layout-forgot-password-success-title')}
            </Title>

            <Paragraph className="!mb-1">
              <Trans i18nKey="layout-forgot-password-success-initiated" components={{ b: <Text strong /> }} />
            </Paragraph>

            <Paragraph className="!mb-1">
              <Trans i18nKey="layout-forgot-password-success-next-login" components={{ b: <Text strong /> }} />
            </Paragraph>

            <Paragraph type="secondary" className="!mb-4">
              {t('layout-forgot-password-success-support')}
            </Paragraph>

            <div className="flex justify-end mt-2">
              <Button type="primary" onClick={onSignOut}>
                {t('common-sign-out')}
              </Button>
            </div>
          </div>
        )}
      </MotionWrapper>
    </Modal>
  );
};
